import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

import StatusChip from "./StatusChip";
import { fmtDate, initials } from "../utils/cvUtils";

export default function CvSubmissionsGrid({
  rows,
  loading,
  onView,
  onEdit,
  onApprove,
  onReject,
}) {
  const theme = useTheme();

  const columns = useMemo(
    () => [
      { field: "employee_code", headerName: "Code", width: 110 },

      {
        field: "name",
        headerName: "Employee",
        flex: 1,
        minWidth: 260,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontWeight: 900,
                fontSize: 14,
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: theme.palette.primary.main,
              }}
            >
              {initials(p.row?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                {p.row?.name || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {p.row?.email || "-"}
              </Typography>
            </Box>
          </Stack>
        ),
      },

      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (p) => <StatusChip row={p.row} />,
      },

      {
        field: "submitted_at",
        headerName: "Submitted",
        width: 190,
        valueFormatter: (value) => fmtDate(value),
      },

      {
        field: "editable_until",
        headerName: "Editable Until",
        width: 240,
        sortable: true,
        renderCell: (p) => {
          const open = !!p.row?.window_open;
          return (
            <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {fmtDate(p.row?.editable_until)}
              </Typography>
              <Chip
                size="small"
                icon={open ? <LockOpenIcon /> : <LockIcon />}
                label={open ? "Open" : "Closed"}
                color={open ? "success" : "default"}
                variant="outlined"
                sx={{
                  fontWeight: 800,
                  borderRadius: 2,
                  bgcolor: alpha(
                    open ? theme.palette.success.main : theme.palette.grey[600],
                    0.12
                  ),
                }}
              />
            </Stack>
          );
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => (
          <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
            <Tooltip title="View">
              <IconButton size="small" onClick={() => onView?.(p.row)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit?.(p.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Approve">
              <IconButton
                size="small"
                onClick={() => onApprove?.(p.row)}
                sx={{ color: theme.palette.success.main }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject">
              <IconButton
                size="small"
                onClick={() => onReject?.(p.row)}
                sx={{ color: theme.palette.error.main }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [theme, onView, onEdit, onApprove, onReject]
  );

  return (
    <DataGrid
      autoHeight
      rows={rows || []}
      columns={columns}
      loading={loading}
      pageSizeOptions={[10, 20, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
      disableRowSelectionOnClick
      getRowId={(r) => r.id}
      rowHeight={64}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 900,
          letterSpacing: 0.3,
        },
        "& .MuiDataGrid-cell": {
          alignItems: "center",
        },
        "& .MuiDataGrid-row:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
    />
  );
}
